import React from 'react';
import { Table, Tag, Empty, Typography, Tooltip } from 'antd';
import { FormattedMessage } from 'react-intl';

const { Text } = Typography;

interface DetectorSignal {
    id: number;
    detectorId: string;
    detectorCategory: string;
    contributionScore: number;
    weight: number;
    confidence: number | null;
    reason: string | null;
    evidence: unknown | null;
}

interface DetectorSignalsTableProps {
    signals: DetectorSignal[];
}

const CATEGORY_COLORS: Record<string, string> = {
    timing: 'blue',
    pattern: 'purple',
    attention: 'magenta',
    device: 'cyan',
    network: 'geekblue',
    text: 'volcano',
    identity: 'gold',
    cohort: 'green',
};

const formatDetectorName = (id: string): string =>
    id.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const DetectorSignalsTable: React.FC<DetectorSignalsTableProps> = ({
    signals,
}) => {
    if (!signals || signals.length === 0) {
        return (
            <Empty
                description={
                    <FormattedMessage id="responseIntegrity.signals.empty" />
                }
            />
        );
    }

    return (
        <Table<DetectorSignal>
            dataSource={signals}
            rowKey="id"
            pagination={false}
            size="small"
            columns={[
                {
                    title: <FormattedMessage id="responseIntegrity.signals.detector" />,
                    dataIndex: 'detectorId',
                    render: (id: string) => <Text strong>{formatDetectorName(id)}</Text>,
                },
                {
                    title: <FormattedMessage id="responseIntegrity.signals.category" />,
                    dataIndex: 'detectorCategory',
                    width: 110,
                    render: (cat: string) => (
                        <Tag color={CATEGORY_COLORS[cat] ?? 'default'} style={{ margin: 0 }}>
                            {cat}
                        </Tag>
                    ),
                },
                {
                    title: <FormattedMessage id="responseIntegrity.signals.contribution" />,
                    dataIndex: 'contributionScore',
                    width: 110,
                    align: 'right',
                    render: (v: number) => (
                        <Text strong style={{ color: v > 0 ? '#fa541c' : undefined }}>
                            -{Number(v).toFixed(1)}
                        </Text>
                    ),
                },
                {
                    title: <FormattedMessage id="responseIntegrity.signals.weight" />,
                    dataIndex: 'weight',
                    width: 80,
                    align: 'right',
                    render: (v: number) => Number(v).toFixed(2),
                },
                {
                    title: <FormattedMessage id="responseIntegrity.signals.confidence" />,
                    dataIndex: 'confidence',
                    width: 100,
                    align: 'right',
                    render: (v: number | null) =>
                        v === null || v === undefined ? '—' : Number(v).toFixed(2),
                },
                {
                    title: <FormattedMessage id="responseIntegrity.signals.reason" />,
                    dataIndex: 'reason',
                    render: (reason: string | null, record) =>
                        reason ? (
                            <Tooltip
                                title={
                                    record.evidence ? (
                                        <pre style={{ margin: 0, fontSize: 11 }}>
                                            {JSON.stringify(record.evidence, null, 2)}
                                        </pre>
                                    ) : null
                                }
                            >
                                <Text>{reason}</Text>
                            </Tooltip>
                        ) : (
                            <Text type="secondary">—</Text>
                        ),
                },
            ]}
        />
    );
};
